import { api } from './client';
import type { ComplianceScore } from './types';
import type { IdeaRunsOn } from '@mycompbox/shared';

/**
 * Epic Request status values
 */
export type EpicRequestStatus = 'pending' | 'approved' | 'rejected' | 'converted' | 'planning';

/**
 * Reaction type values
 */
export type ReactionType = 'like' | 'fire' | 'dislike';

/**
 * Structured description for Epic Requests
 */
export interface EpicRequestStructuredDesc {
  problemStatement: string;
  proposedSolution: string;
  impactAssessment: string;
  targetAudience?: string;
  successMetrics?: string;
  alternatives?: string;
  dependencies?: string;
  estimatedEffort?: string;
  /** ENG-313 — the database the work should use, from the request interview. */
  databaseChoice?: 'free_hosted' | 'cloud_paid' | 'existing' | 'none';
  /** ENG-313 — free-text detail about the database choice. */
  databaseNote?: string;
}

/** Human-readable labels for {@link EpicRequestStructuredDesc.databaseChoice}. */
export const DATABASE_CHOICE_LABELS: Record<NonNullable<EpicRequestStructuredDesc['databaseChoice']>, string> = {
  free_hosted: 'Free hosted database',
  cloud_paid: 'Paid cloud database',
  existing: 'Existing database',
  none: 'No database needed',
};

export type PlanningStage = 'ANALYZE' | 'DECOMPOSE' | 'DETAIL' | 'EVALUATE' | 'VERIFY';
export type PlanningStageStatus = 'pending' | 'active' | 'completed' | 'failed' | 'skipped';

export interface PlanningStageEntry {
  name: PlanningStage;
  status: PlanningStageStatus;
  startedAt?: string;
  completedAt?: string;
  details?: string;
}

export interface QualityScore {
  overall: number;
  epicDescription: number;
  structure: number;
  detail: number;
  scoping: number;
}

export interface PlanningState {
  currentStage: PlanningStage;
  stages: PlanningStageEntry[];
  epicId?: string;
  epicIdentifier?: string;
  qualityScore?: QualityScore;
  error?: {
    stage: PlanningStage;
    message: string;
    timestamp: string;
  };
}

/** An active planning or build job, as the ideas list reports it. */
export interface IdeaJobFacts {
  /** The job's id, for a link to its live run. */
  id: string;
  status: string;
  waitingReason: string | null;
  needsInput: { kind: string | null } | null;
}

/**
 * Build facts for one request (GET /epic-requests?withFacts=true): its
 * blueprint, active jobs and open pull requests. The new look works out the
 * idea's stage from these.
 */
export interface IdeaFacts {
  epic: {
    id: string;
    state: 'active' | 'implemented' | 'archived';
    displayState: 'follow_up' | 'merged' | 'running' | 'ready' | 'needs_decision' | 'failed' | 'none';
    featureCount: number;
    completedFeatureCount: number;
  } | null;
  planJob: IdeaJobFacts | null;
  buildJob: IdeaJobFacts | null;
  /**
   * The newest build job, finished or not (ENG-364). The idea page picks its
   * composer mode from its status and lets only its owner send. Absent from
   * older servers.
   */
  latestBuildJob?: (IdeaJobFacts & { owner: { id: string; name: string } }) | null;
  openPullRequests: number;
}

/**
 * Epic Request entity
 */
export interface EpicRequest {
  id: string;
  identifier?: string | null;
  title: string;
  description?: string;
  structuredDesc?: EpicRequestStructuredDesc;
  originatingTicketUrl?: string | null;
  /** Where this idea runs (ER-145); null = never chosen. */
  runsOn?: IdeaRunsOn | null;
  planningState?: string | PlanningState | null;
  status: EpicRequestStatus;
  requestedById: string;
  personalScopeId?: string | null;
  teamId?: string | null;
  team?: { id: string; name: string; key: string; level?: string | null; gitRemote?: string | null } | null;
  convertedEpicId?: string | null;
  convertedEpic?: {
    id: string;
    name: string;
    identifier: string | null;
  } | null;
  requestedBy?: {
    id: string;
    name: string;
    email: string;
    avatarUrl?: string | null;
  };
  complianceScore?: ComplianceScore | null;
  /** Only with `withFacts: true` on the list. */
  facts?: IdeaFacts | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Summary of the Interview linked to an Epic Request (ENG-147).
 * Returned by the owner-scoped GET /epic-requests/:id/interview endpoint so the
 * ER detail view can deep-link to the driving mailbox job at
 * /mailbox/jobs/<jobId>.
 */
export interface LinkedInterviewSummary {
  interviewId: string;
  jobId: string | null;
  status: string;
}

/**
 * Transfer direction for moving epic requests between scopes
 */
export type TransferDirection = 'personal-to-team' | 'team-to-personal';

/**
 * Input for transferring an epic request between scopes.
 * teamId is required when transferring to team scope.
 */
export type TransferEpicRequestInput =
  | { direction: 'personal-to-team'; teamId: string }
  | { direction: 'team-to-personal' };

/**
 * Input for transferring an epic between scopes
 */
export interface TransferEpicInput {
  direction: TransferDirection;
  teamId?: string;
}

/**
 * Input for creating a personal epic request
 */
export interface CreatePersonalEpicRequestInput {
  title: string;
  description?: string;
  structuredDesc?: EpicRequestStructuredDesc;
  originatingTicketUrl?: string;
  /** Where this idea runs (ER-145). */
  runsOn?: IdeaRunsOn;
}

/**
 * Epic Request with aggregated reaction counts
 */
export interface EpicRequestWithReactionCounts extends EpicRequest {
  reactionCounts: {
    reactionType: string;
    count: number;
  }[];
  userReaction?: string | null;
}

/**
 * Comment entity
 */
export interface EpicRequestComment {
  id: string;
  content: string;
  epicRequestId: string;
  authorId: string;
  author: {
    id: string;
    name: string;
    email: string;
    avatarUrl?: string | null;
  };
  createdAt: string;
  updatedAt: string;
}

/**
 * Paginated response structure
 */
export interface PaginatedEpicRequestsResponse {
  data: EpicRequestWithReactionCounts[];
  meta: {
    cursor: string | null;
    hasMore: boolean;
    totalCount?: number;
  };
}

/**
 * Paginated comments response structure
 */
export interface PaginatedCommentsResponse {
  data: EpicRequestComment[];
  meta: {
    cursor: string | null;
    hasMore: boolean;
    totalCount?: number;
  };
}

/**
 * Filters for listing epic requests
 */
export interface EpicRequestFilters {
  cursor?: string;
  limit?: number;
  status?: EpicRequestStatus;
  /** Comma-separated statuses; the server keeps requests with any of them. */
  statuses?: string;
  /** Title or identifier contains this text (server-side, across all pages). */
  search?: string;
  requestedById?: string;
  /** Only requests in this project (team). */
  teamId?: string;
  orderBy?: 'createdAt' | 'updatedAt' | 'title' | undefined;
  orderDirection?: 'asc' | 'desc' | undefined;
  /** Add each request's build facts (new look). */
  withFacts?: boolean;
  /** Comma-separated idea stages: idea, blueprint, build, done, closed (new look). */
  stages?: string;
  /** Only ideas that need the user (new look). */
  needsYou?: boolean;
}

/** How many ideas are in each stage (GET /epic-requests/stage-counts). */
export interface IdeaStageCounts {
  idea: number;
  blueprint: number;
  build: number;
  done: number;
  closed: number;
  needsYou: number;
  total: number;
}

export type IdeaStageCountFilters = Pick<EpicRequestFilters, 'status' | 'statuses' | 'search' | 'requestedById' | 'teamId'>;

/**
 * Filters for listing comments
 */
export interface CommentFilters {
  cursor?: string;
  limit?: number;
}

/**
 * Input for creating a new epic request
 */
export interface CreateEpicRequestInput {
  title: string;
  /** The project (team) the request belongs to; the API requires it. */
  teamId?: string;
  description?: string;
  structuredDesc?: EpicRequestStructuredDesc;
  originatingTicketUrl?: string;
  /** Where this idea runs (ER-145). */
  runsOn?: IdeaRunsOn;
}

/**
 * Input for updating an epic request
 */
export interface UpdateEpicRequestInput {
  title?: string;
  description?: string;
  structuredDesc?: EpicRequestStructuredDesc;
  status?: EpicRequestStatus;
  originatingTicketUrl?: string;
  /** Where this idea runs (ER-145); null clears it. */
  runsOn?: IdeaRunsOn | null;
}

/**
 * Input for adding a reaction
 */
export interface AddReactionInput {
  reactionType: ReactionType;
}

/**
 * Input for creating a comment
 */
export interface CreateCommentInput {
  content: string;
}

/**
 * Input for updating a comment
 */
export interface UpdateCommentInput {
  content: string;
}

/**
 * API client for Epic Requests
 */
/**
 * The detail endpoint sends structuredDesc as JSON text; read it into an
 * object so pages can use its sections (left as is when already an object).
 */
export function withParsedDesc<T extends { data: { structuredDesc?: EpicRequestStructuredDesc } }>(
  response: T
): T {
  const raw: unknown = response.data.structuredDesc;
  if (typeof raw !== 'string') return response;
  let parsed: EpicRequestStructuredDesc | undefined;
  try {
    const value = JSON.parse(raw) as unknown;
    parsed = value && typeof value === 'object' ? (value as EpicRequestStructuredDesc) : undefined;
  } catch {
    parsed = undefined;
  }
  const { structuredDesc: _raw, ...rest } = response.data;
  return { ...response, data: parsed ? { ...rest, structuredDesc: parsed } : rest };
}

export const epicRequestsApi = {
  /**
   * List epic requests with cursor-based pagination
   */
  list: (filters: EpicRequestFilters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined) params.append(key, String(value));
    });
    const queryString = params.toString();
    return api.get<PaginatedEpicRequestsResponse>(
      `/epic-requests${queryString ? `?${queryString}` : ''}`
    );
  },

  /**
   * Count ideas by stage (new look's stage chips)
   */
  stageCounts: (filters: IdeaStageCountFilters = {}) => {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(filters)) {
      if (value) params.append(key, value);
    }
    const queryString = params.toString();
    return api.get<{ data: IdeaStageCounts }>(`/epic-requests/stage-counts${queryString ? `?${queryString}` : ''}`);
  },

  /**
   * Get a single epic request by ID
   */
  get: (id: string) =>
    api
      .get<{ data: EpicRequestWithReactionCounts }>(`/epic-requests/${id}`)
      .then(withParsedDesc),

  /** One request with its build facts (the new look's idea page). */
  getWithFacts: (id: string) =>
    api
      .get<{ data: EpicRequestWithReactionCounts }>(`/epic-requests/${id}?withFacts=true`)
      .then(withParsedDesc),

  /**
   * Create a new epic request
   */
  create: (input: CreateEpicRequestInput) =>
    api.post<{ data: EpicRequest }>('/epic-requests', input),

  /**
   * Update an existing epic request
   */
  update: (id: string, input: UpdateEpicRequestInput) =>
    api.put<{ data: EpicRequest }>(`/epic-requests/${id}`, input),

  /**
   * Delete an epic request
   */
  delete: (id: string) => api.delete(`/epic-requests/${id}`),

  /**
   * Approve an epic request (admin or creator)
   */
  approve: (id: string) =>
    api.post<{ data: EpicRequest }>(`/epic-requests/${id}/approve`, {}),

  /**
   * Reject an epic request (admin or creator)
   */
  reject: (id: string) =>
    api.post<{ data: EpicRequest }>(`/epic-requests/${id}/reject`, {}),

  /**
   * Add or update a reaction on an epic request
   */
  react: (id: string, input: AddReactionInput) =>
    api.post<{ message: string; data: { reactionType: string } }>(
      `/epic-requests/${id}/reactions`,
      input
    ),

  /**
   * Remove user's reaction from an epic request
   */
  removeReaction: (id: string) =>
    api.delete(`/epic-requests/${id}/reactions`),

  /**
   * List comments for an epic request
   */
  listComments: (id: string, filters: CommentFilters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined) params.append(key, String(value));
    });
    const queryString = params.toString();
    return api.get<PaginatedCommentsResponse>(
      `/epic-requests/${id}/comments${queryString ? `?${queryString}` : ''}`
    );
  },

  /**
   * Create a comment on an epic request
   */
  createComment: (id: string, input: CreateCommentInput) =>
    api.post<{ data: EpicRequestComment }>(
      `/epic-requests/${id}/comments`,
      input
    ),

  /**
   * Update a comment
   */
  updateComment: (
    epicRequestId: string,
    commentId: string,
    input: UpdateCommentInput
  ) =>
    api.put<{ data: EpicRequestComment }>(
      `/epic-requests/${epicRequestId}/comments/${commentId}`,
      input
    ),

  /**
   * Delete a comment
   */
  deleteComment: (epicRequestId: string, commentId: string) =>
    api.delete(`/epic-requests/${epicRequestId}/comments/${commentId}`),

  /**
   * Create a personal epic request (pending approval)
   */
  createPersonal: (input: CreatePersonalEpicRequestInput) =>
    api.post<{ data: EpicRequest }>('/me/epic-requests', input),

  /**
   * List personal epic requests
   */
  listPersonal: (filters: EpicRequestFilters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined) params.append(key, String(value));
    });
    const queryString = params.toString();
    return api.get<PaginatedEpicRequestsResponse>(
      `/me/epic-requests${queryString ? `?${queryString}` : ''}`
    );
  },

  /**
   * Transfer an epic request between personal and team scope
   */
  transfer: (id: string, input: TransferEpicRequestInput) =>
    api.post<{ data: EpicRequest }>(`/epic-requests/${id}/transfer`, input),

  /**
   * Get the interview linked to an epic request (ENG-147), owner-scoped.
   * Returns { interviewId, jobId, status } or null when none is linked.
   * A non-owner (non-admin) receives a 403 from the API.
   */
  getInterview: (id: string) =>
    api.get<{ data: LinkedInterviewSummary | null }>(
      `/epic-requests/${id}/interview`
    ),
};
